import { Box, Button, Input, Stack, Link } from '@chakra-ui/react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import AuthHeader from '@/components/auth-header';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import { useAuth } from '@/lib/hooks/use-auth';
import useFeatureFlag from '@/lib/hooks/use-feature-flag';

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email address' }),
  password: z
    .string({ required_error: 'Password is required' })
    .min(8, { message: 'Password must be at least 8 characters' }),
});

type FormValues = z.infer<typeof formSchema>;

const Login = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const ssoEnabled = useFeatureFlag('sso');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormValues) => {
    try {
      const res = await auth.login(data.email, data.password.trim());

      navigate(`/${res.default_workspace_id}/home`);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err?.response?.data?.error || 'Invalid email or password. Please try again.',
      });
    }
  };

  return (
    <Stack gap={6}>
      <AuthHeader title='Log in' />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <Field label='Email' invalid={!!errors.email} errorText={errors.email?.message}>
            <Input {...register('email')} type={'email'} />
          </Field>
          <Field label='Password' invalid={!!errors.password} errorText={errors.password?.message}>
            <Input {...register('password')} type={'password'} />
          </Field>
          <Box textAlign={'end'} width={'full'}>
            <Link fontSize={'sm'} float={'right'} asChild>
              <NavLink to='/auth/forgot-password'>Forgot Password?</NavLink>
            </Link>
          </Box>
          <Button type={'submit'} width={'full'} loading={isSubmitting} loadingText='Loading...'>
            Login
          </Button>
          {ssoEnabled && (
            <Button variant={'outline'} width={'full'} asChild>
              <NavLink to='/auth/sso'>Sign in with SSO</NavLink>
            </Button>
          )}
        </Stack>
      </form>
      <Box textAlign={'center'}>
        <Button fontSize={'sm'} variant={'ghost'} asChild>
          <NavLink to='/auth/register'>Need an account?</NavLink>
        </Button>
      </Box>
    </Stack>
  );
};

export default Login;
